import React from 'react';
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined';
import DangerousOutlinedIcon from '@mui/icons-material/DangerousOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import { type SxProps, type Theme } from '@mui/material';

import type { StatusLight } from '../types';

/**
 * Colorblind-friendly eligibility status icons.
 *
 * Each status uses a distinct **shape + symbol** so users can distinguish
 * them without relying on color alone:
 *
 *  GREEN  → Circle + checkmark   (CheckCircleOutlined)
 *  YELLOW → Triangle + exclamation (WarningAmberOutlined)
 *  RED    → Octagon + X           (DangerousOutlined)
 */

const STATUS_ICON_CONFIG: Record<
  StatusLight,
  {
    Icon: typeof CheckCircleOutlinedIcon;
    palette: 'success' | 'warning' | 'error';
    label: string;
  }
> = {
  GREEN: {
    Icon: CheckCircleOutlinedIcon,
    palette: 'success',
    label: 'Eligible',
  },
  YELLOW: {
    Icon: WarningAmberOutlinedIcon,
    palette: 'warning',
    label: 'Needs review',
  },
  RED: {
    Icon: DangerousOutlinedIcon,
    palette: 'error',
    label: 'Ineligible',
  },
};

interface EligibilityStatusIconProps {
  status: StatusLight;
  /** Icon size in px. Defaults to 16. */
  size?: number;
  sx?: SxProps<Theme>;
}

export const EligibilityStatusIcon: React.FC<EligibilityStatusIconProps> = ({
  status,
  size = 16,
  sx,
}) => {
  const { Icon, palette, label } = STATUS_ICON_CONFIG[status];

  return (
    <Icon
      aria-label={label}
      titleAccess={label}
      sx={[
        {
          fontSize: size,
          color: `${palette}.main`,
          flexShrink: 0,
        },
        // Merge caller sx
        ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
      ]}
    />
  );
};
